import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { customerAPI } from '@/services/customerAPI';
import { awsAPI } from '@/services/awsAPI';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  FileText,
  Download,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Filter,
  Search
} from 'lucide-react';
import { formatCurrency } from '@/utils/searchValidation';
import { cn } from '@/lib/utils';
import { logger } from '@/utils/logger';
import type { ProcessingType, JobStatus } from '@/types';

const PROCESSING_TYPE_LABELS: Record<ProcessingType, string> = {
  validate: 'Validation',
  enrich: 'Enrichment',
  enrich_validate: 'Enrich + Validate',
  scoring: 'Scoring',
  scoring_enrich: 'Scoring + Enrich',
};

const STATUS_CONFIG: Record<JobStatus, { icon: React.ComponentType<{ className?: string }>; color: string; label: string }> = {
  completed: { icon: CheckCircle, color: 'text-green-600 bg-green-100', label: 'Completed' },
  processing: { icon: Clock, color: 'text-slate-600 bg-slate-100', label: 'Processing' },
  failed: { icon: XCircle, color: 'text-red-600 bg-red-100', label: 'Failed' },
  pending: { icon: Clock, color: 'text-slate-600 bg-slate-100', label: 'Pending' },
};

const DOWNLOAD_WINDOW_DAYS = 90;
const EXPIRING_WARNING_DAYS = 75;

export const BatchHistoryPage: React.FC = () => {
  const { user } = useCustomerAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<JobStatus | 'all'>('all');
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);

  const { data: batches, isLoading, error } = useQuery({
    queryKey: ['batch-history', user?.userId],
    queryFn: () => customerAPI.getBatchHistory(user!.userId, 100),
    enabled: !!user?.userId,
  });

  const filteredBatches = batches?.filter((batch) => {
    const matchesSearch = batch.fileName?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false;
    const matchesStatus = statusFilter === 'all' || batch.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleDownload = async (jobId: string, fileName: string, format: 'csv' | 'json' = 'csv') => {
    try {
      setDownloadingJobId(jobId);
      logger.info('Requesting fresh download URL', { jobId, fileName, format });

      // Get a fresh presigned URL from the batch processing backend (valid for 1 hour)
      const { downloadUrl } = await awsAPI.getDownloadUrl(jobId, { format });

      logger.info('Opening download URL', { jobId });
      window.open(downloadUrl, '_blank');
    } catch (error) {
      logger.error('Failed to download batch results:', error);
      alert('Failed to download batch results. Please try again.');
    } finally {
      setDownloadingJobId(null);
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-10 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Failed to Load Batch History</h2>
          <p className="text-slate-600 mb-4">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Batch History</h1>
        <p className="text-sm text-slate-600">
          View and download your processed batch files
        </p>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by filename..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-collectability-blue focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-600" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as JobStatus | 'all')}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-collectability-blue focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="processing">Processing</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>

        {filteredBatches && filteredBatches.length > 0 && (
          <div className="mt-3 text-xs text-slate-600">
            Showing {filteredBatches.length} of {batches?.length || 0} batches
          </div>
        )}
      </Card>

      {/* Batch List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <Card key={i} className="p-3 animate-pulse">
              <div className="h-20 bg-slate-100 rounded" />
            </Card>
          ))}
        </div>
      ) : filteredBatches && filteredBatches.length > 0 ? (
        <div className="space-y-3">
          {filteredBatches.map((batch) => {
            const StatusIcon = STATUS_CONFIG[batch.status].icon;

            // Calculate days since upload for expiration warning
            const uploadDate = new Date(batch.uploadedAt);
            const daysSinceUpload = Math.floor((Date.now() - uploadDate.getTime()) / (1000 * 60 * 60 * 24));
            const daysRemaining = DOWNLOAD_WINDOW_DAYS - daysSinceUpload;
            const isExpiringSoon = daysSinceUpload >= EXPIRING_WARNING_DAYS && daysSinceUpload < DOWNLOAD_WINDOW_DAYS;
            const hasExpired = daysSinceUpload >= DOWNLOAD_WINDOW_DAYS;

            return (
              <Card
                key={batch.id}
                className={cn(
                  "p-3 transition-shadow",
                  hasExpired && "border-red-300 bg-red-50",
                  isExpiringSoon && !hasExpired && "border-orange-300 bg-orange-50"
                )}
              >
                {/* Expiring Soon or Expired Warning Banner */}
                {hasExpired ? (
                  <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded">
                    <div className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-red-900">
                          Download Expired
                        </p>
                        <p className="text-xs text-red-800">
                          This batch exceeded the 90-day download window. Reprocess to access results again.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : isExpiringSoon ? (
                  <div className="mb-3 p-2 bg-orange-100 border border-orange-300 rounded">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-orange-900">
                          Download Expiring Soon
                        </p>
                        <p className="text-xs text-orange-800">
                          This batch will be unavailable in <strong>{daysRemaining} days</strong>. Download now.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                  {/* File Icon and Info */}
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-slate-600" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-slate-900 mb-0.5 truncate">
                        {batch.fileName}
                      </h3>

                      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 mb-1">
                        <span>{PROCESSING_TYPE_LABELS[batch.processingType]}</span>
                        <span>•</span>
                        <span>{batch.successfulRows} of {batch.rowCount} rows</span>
                        <span>•</span>
                        <span>{formatCurrency(batch.cost)}</span>
                      </div>

                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className={cn(
                          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border',
                          batch.status === 'completed' && 'bg-green-50 text-green-700 border-green-200',
                          batch.status === 'processing' && 'bg-blue-50 text-blue-700 border-blue-200',
                          batch.status === 'failed' && 'bg-red-50 text-red-700 border-red-200',
                          batch.status === 'pending' && 'bg-slate-50 text-slate-700 border-slate-200'
                        )}>
                          {STATUS_CONFIG[batch.status].label}
                        </span>

                        {batch.failedRows > 0 && (
                          <span className="text-xs text-orange-600">
                            {batch.failedRows} failed
                          </span>
                        )}

                        {hasExpired ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                            Expired
                          </span>
                        ) : isExpiringSoon ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                            {daysRemaining}d left
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="flex flex-col gap-0.5 text-xs text-slate-600 lg:text-right">
                    <div>Uploaded: {new Date(batch.uploadedAt).toLocaleDateString()}</div>
                    {batch.completedAt && (
                      <div>Completed: {new Date(batch.completedAt).toLocaleDateString()}</div>
                    )}
                  </div>

                  {/* Download Button */}
                  <div className="flex items-center gap-2 lg:flex-shrink-0">
                    {hasExpired ? (
                      <Button variant="outline" disabled size="sm" className="text-red-600 border-red-300 h-8 text-xs">
                        Expired
                      </Button>
                    ) : batch.status === 'completed' ? (
                      <Button
                        onClick={() => handleDownload(batch.jobId, batch.fileName)}
                        className="gap-1.5 h-8 text-xs"
                        size="sm"
                        disabled={downloadingJobId === batch.jobId}
                      >
                        <Download className="w-3.5 h-3.5" />
                        {downloadingJobId === batch.jobId ? 'Downloading...' : 'Download'}
                      </Button>
                    ) : batch.status === 'processing' ? (
                      <Button variant="outline" disabled size="sm" className="h-8 text-xs">
                        Processing...
                      </Button>
                    ) : batch.status === 'failed' ? (
                      <Button variant="outline" disabled size="sm" className="h-8 text-xs">
                        Failed
                      </Button>
                    ) : (
                      <Button variant="outline" disabled size="sm" className="h-8 text-xs">
                        Pending
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-10 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-slate-900 mb-2">
            {searchQuery || statusFilter !== 'all' ? 'No Batches Found' : 'No Batch History'}
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Upload your first batch to get started'}
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <Button onClick={() => window.location.href = '/batch'} size="sm" className="h-8 text-xs">
              Upload a Batch
            </Button>
          )}
        </Card>
      )}

      {/* Info Card */}
      <Card className="p-3 mt-6 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-slate-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-xs font-semibold text-slate-900 mb-1">
              Download Availability - 90 Day Window
            </h3>
            <p className="text-xs text-blue-800 mb-1">
              Batch results are available for download for <strong>90 days</strong> after completion.
            </p>
            <ul className="text-xs text-blue-800 space-y-0.5 list-disc list-inside">
              <li>Email reminder at 75 days to download your results</li>
              <li>Final notice at 90 days when downloads expire</li>
              <li>After 90 days, batches remain visible but downloads are disabled</li>
              <li>To access expired results, reprocess the batch</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

